/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { Box, CircularProgress, FormControl, FormHelperText, Grid, InputAdornment, TextField } from '@mui/material'
import Typography from '@mui/material/Typography'
import Joi from 'joi'
import qs from 'qs'
import React, { useEffect, useState } from 'react'
import { useAsyncHandler } from 'react-hooks-async-handlers'
import { useDispatch, useSelector } from 'react-redux'
import { StepComponentProps } from 'react-step-builder'
import { load } from 'recaptcha-v3'
import config from '../../config'
import stepIcon from '../../images/step-illustration-email.png'
import analyst from '../../libs/analyst'
import analytics from '../../libs/analytics'
import api from '../../libs/api'
import apiErrorUtil from '../../libs/errors/apiErrorUtil'
import navigationUtil from '../../libs/navigationUtil'
import { ReduxState } from '../../reducers'
import FormDispatcher from '../../reducers/form/dispatcher'
import EmailSvgIcon from './assets/svg/EmailSvgIcon'
import InputAdornmentSVGWrapper from './common/InputAdornmentSVGWrapper'
import NavigationFooter from './common/NavigationFooter'
import OnboardStepper from './common/OnboardStepper'

const validateEmail = Joi.string()
  .email({ tlds: { allow: false } })
  .label('Email')

function StepEmail(props: StepComponentProps) {
  const { form } = useSelector((state: ReduxState) => ({
    form: state.form,
  }))
  const [error, setError] = useState('')
  const [email, setEmail] = useState(form.email || '')
  const dispatch = useDispatch()

  function checkIsValid(emailToCheck: string) {
    const formError = validateEmail.validate(emailToCheck)
    if (formError.error) setError(formError.error.message)
    return !formError.error
  }

  const actionCheckEmail = useAsyncHandler(async () => {
    let isEmailAvailable = false
    try {
      const recaptcha = await load(config.recaptchaSiteKey)
      const token = await recaptcha.execute('onboard_email')

      const { data } = await api.onboardCheckEmailCreate({ email, recaptcha: token, analytics_ref: analyst.client_id })
      if (data.redirect) {
        analytics.sendEvent('page@StepEnd')
        navigationUtil.navigateToHrLogin(email)
      } else {
        isEmailAvailable = true
      }
    } catch (e) {
      const { status } = apiErrorUtil.getRequestInfo(e)
      const code = apiErrorUtil.getErrorCodeFromApi(e)

      console.log('error:', status, code)

      throw e
    }

    return isEmailAvailable
  })

  const handlePressNext = async () => {
    analytics.sendEvent('Onboard@EmailStep.ClickNext', {}, { email })

    setError('')
    if (!checkIsValid(email)) return null

    const isEmailAvailable = await actionCheckEmail.execute()
    if (!isEmailAvailable) {
      // here we have validation issue or auto redirect to hr
      return null
    }

    dispatch(FormDispatcher.actions.setEmail(email))

    analytics.sendEvent('Onboard@EmailProvided', {}, { email })
    props.next()
  }

  useEffect(() => {
    analytics.sendEvent('page@StepEmail')

    const qStr = location.search || ''
    const params = qs.parse(qStr.replace('?', '')) || {}
    const { step } = params
    if (step === 'phone') props.jump(5)
    if (step === 'sms') props.jump(6)
  }, [])

  const handleEmailInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault() // preventDefault should be here, otherwise onChange event will not fire
      handlePressNext()
    }
  }

  const handleTextChanges = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(event.target.value || '')
    if (error) setError('')
    if (actionCheckEmail.error) actionCheckEmail.reset()
  }

  const errorMessage = error || actionCheckEmail.error

  return (
    <Grid container direction="column">
      <Grid item sx={{ mb: { xs: 3, sm: 4, md: 4 } }}>
        <OnboardStepper activeStep={0} />
      </Grid>
      <Grid item>
        <Grid container direction="row" spacing={5} justifyContent="spaceBetween">
          <Grid item xs={12} sm={12} md={7}>
            <Box>
              <Typography variant="h1">Welcome to Reaction</Typography>
              <Typography variant="subtitle1">
                Let’s start creating your Reaction Club app.
              </Typography>
            </Box>
            <Box
              sx={{ mt: { xs: 6, md: 12 } }}
              css={css`
                min-height: 180px;
              `}
            >
              <Grid component="form" container direction="column" sx={{ maxWidth: 500 }}>
                <FormControl error={!!errorMessage}>
                  <TextField
                    autoFocus
                    autoComplete="off"
                    required
                    id="email"
                    type="email"
                    label="Your work email"
                    value={email}
                    placeholder="name@company.com"
                    variant="outlined"
                    onChange={handleTextChanges}
                    error={!!errorMessage}
                    // helperText={(touchedFields.has('email') || formValidated) && formErrors.email?.message}
                    onKeyDown={handleEmailInputKeyDown}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <InputAdornmentSVGWrapper>
                            <EmailSvgIcon />
                          </InputAdornmentSVGWrapper>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormHelperText
                    css={css`
                      padding-left: 20px;
                    `}
                  >
                    {errorMessage}
                  </FormHelperText>
                </FormControl>

                <Grid container justifyContent={'center'}>
                  <Typography variant={'caption'} textAlign={'center'}>
                    No spam emails, promise
                  </Typography>
                </Grid>

                {actionCheckEmail.isLoading ? (
                  <Grid
                    container
                    justifyContent={'center'}
                    css={css`
                      padding-top: 30px;
                    `}
                  >
                    <CircularProgress />
                  </Grid>
                ) : null}
              </Grid>
            </Box>
          </Grid>
          <Grid item md={5} sx={{ mb: 6, display: { xs: 'none', sm: 'none', md: 'block' } }}>
            <img src={stepIcon} alt="Step email" width={354} height={343} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <NavigationFooter {...props} onPressNext={handlePressNext} disabled={actionCheckEmail.isLoading} />
      </Grid>
    </Grid>
  )
}

export default StepEmail
